import { useState, useEffect, useRef, useCallback } from "react";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { BRAND } from "../lib/brand";

// ─── Data ─────────────────────────────────────────────────────────────────────
const BOOKMAKERS = [
  { id: 1, name: "Hard Rock Bet", domain: "hardrock.bet",    region: "USA",    odds: "2.35", link: "https://www.hardrock.bet",    bg: "#1A0808", fg: "#FFD700" },
  { id: 2, name: "DraftKings",    domain: "draftkings.com",  region: "USA",    odds: "2.18", link: "https://www.draftkings.com",  bg: "#0D1B3E", fg: "#53D337" },
  { id: 3, name: "FanDuel",       domain: "fanduel.com",     region: "USA",    odds: "2.25", link: "https://www.fanduel.com",     bg: "#003A8C", fg: "#FFFFFF" },
  { id: 4, name: "Betano",        domain: "betano.com",      region: "LATAM",  odds: "2.40", link: "https://www.betano.com",      bg: "#1A0020", fg: "#FF7300" },
  { id: 5, name: "Codere",        domain: "codere.com",      region: "LATAM",  odds: "2.15", link: "https://www.codere.com",      bg: "#003D1A", fg: "#00C853" },
  { id: 6, name: "Bet365",        domain: "bet365.com",      region: "Europa", odds: "2.28", link: "https://www.bet365.com",      bg: "#025C3B", fg: "#FFFFFF" },
  { id: 7, name: "Betway",        domain: "betway.com",      region: "Europa", odds: "2.20", link: "https://www.betway.com",      bg: "#2A003D", fg: "#00CC70" },
  { id: 8, name: "1xBet",         domain: "1xbet.com",       region: "Europa", odds: "2.45", link: "https://www.1xbet.com",       bg: "#001A6B", fg: "#FFFFFF" },
];

const REGION_DOT = { USA: "#4D9FFF", LATAM: "#FF7300", Europa: "#00D97E" };

// ─── Logo: branded pill with brand colors, no external image dependency ────────
function BookmakerLogo({ name, domain, bg, fg }) {
  // Slim text that shows the name cleanly in brand colors
  const display = {
    "Hard Rock Bet": "Hard Rock",
    "DraftKings": "DraftKings",
    "FanDuel": "FanDuel",
    "Betano": "Betano",
    "Codere": "Codere",
    "Bet365": "bet365",
    "Betway": "betway",
    "1xBet": "1xBet",
  }[name] || name;

  return (
    <div style={{
      width: 100, height: 30,
      background: bg,
      borderRadius: 6,
      display: "flex", alignItems: "center", justifyContent: "center",
      margin: "0 auto",
      border: `1px solid ${fg}30`,
    }}>
      <span style={{
        color: fg,
        fontSize: display.length > 8 ? 10 : 12,
        fontWeight: 900,
        letterSpacing: display.length > 8 ? 0 : "-0.02em",
        fontFamily: "'Outfit', system-ui, sans-serif",
        whiteSpace: "nowrap",
      }}>
        {display}
      </span>
    </div>
  );
}

// ─── Single Card ──────────────────────────────────────────────────────────────
function BookmakerCard({ book, cardWidth }) {
  const [hovered, setHovered] = useState(false);
  const dotColor = REGION_DOT[book.region] || "#D4AF37";

  return (
    <div style={{ width: cardWidth, flexShrink: 0, padding: "0 5px", boxSizing: "border-box" }}>
      <a
        href={`${book.link}?ref=${BRAND.slug}`}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`bookmaker-card-${book.id}`}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          display: "flex", flexDirection: "column", alignItems: "center",
          gap: 7, textDecoration: "none",
          padding: "13px 8px 11px",
          borderRadius: 10,
          background: hovered ? "#18181F" : "#111116",
          border: "1px solid rgba(255,255,255,0.06)",
          boxShadow: hovered
            ? "0 0 0 1px rgba(212,175,55,0.45), 0 8px 24px rgba(0,0,0,0.5)"
            : "none",
          transform: hovered ? "translateY(-3px)" : "translateY(0)",
          transition: "all 0.2s ease",
          cursor: "pointer",
        }}
      >
        {/* Official-looking brand logo */}
        <BookmakerLogo name={book.name} domain={book.domain} bg={book.bg} fg={book.fg} />

        {/* Region — tiny dot + label */}
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ width: 5, height: 5, borderRadius: "50%", background: dotColor, display: "inline-block", flexShrink: 0 }} />
          <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.05em", color: "#52525B", textTransform: "uppercase" }}>
            {book.region}
          </span>
        </div>

        {/* Odds */}
        <div style={{
          fontSize: 24, fontWeight: 900, lineHeight: 1,
          fontFamily: "'JetBrains Mono', monospace",
          color: "#10B981",
        }}>
          {book.odds}
        </div>

        {/* CTA — gold + staggered pulse (no accordion effect) */}
        <div
          className="apostar-btn-pulse"
          style={{
            width: "100%", padding: "5px 0",
            background: hovered ? "#D4AF37" : "rgba(212,175,55,0.12)",
            borderRadius: 6, textAlign: "center",
            fontSize: 9, fontWeight: 800, letterSpacing: "0.1em",
            color: hovered ? "#000" : "#D4AF37",
            textTransform: "uppercase",
            transition: "all 0.18s",
            animationDelay: `${(book.id - 1) * 0.4}s`,
          }}
        >
          Apostar
        </div>
      </a>
    </div>
  );
}

// ─── Carousel ─────────────────────────────────────────────────────────────────
export default function BookmakersCarousel() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [cardsPerView, setCardsPerView] = useState(5);
  const [containerWidth, setContainerWidth] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const wrapperRef = useRef(null);
  const touchStartX = useRef(null);

  const totalSlides = Math.ceil(BOOKMAKERS.length / cardsPerView);
  const cardWidth = containerWidth > 0 ? containerWidth / cardsPerView : 160;

  const measure = useCallback(() => {
    if (wrapperRef.current) setContainerWidth(wrapperRef.current.offsetWidth);
  }, []);

  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  useEffect(() => {
    const update = () => {
      if (window.innerWidth < 480)       setCardsPerView(2);
      else if (window.innerWidth < 768)  setCardsPerView(3);
      else if (window.innerWidth < 1024) setCardsPerView(4);
      else                               setCardsPerView(5);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  useEffect(() => { setCurrentSlide(0); }, [cardsPerView]);

  useEffect(() => {
    if (isPaused) return;
    const t = setInterval(() => {
      setCurrentSlide((p) => (p >= totalSlides - 1 ? 0 : p + 1));
    }, 5000);
    return () => clearInterval(t);
  }, [isPaused, totalSlides]);

  const next = () => setCurrentSlide((p) => (p >= totalSlides - 1 ? 0 : p + 1));
  const prev = () => setCurrentSlide((p) => (p <= 0 ? totalSlides - 1 : p - 1));
  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const delta = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(delta) > 40) delta > 0 ? next() : prev();
    touchStartX.current = null;
  };

  const translateXpx = -(currentSlide * cardWidth * cardsPerView);

  const btnStyle = {
    width: 24, height: 24, borderRadius: 5,
    background: "rgba(212,175,55,0.08)",
    border: "1px solid rgba(212,175,55,0.2)",
    color: "#D4AF37", cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
    transition: "background 0.2s",
  };

  return (
    <section
      data-testid="bookmakers-carousel"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      style={{ padding: "16px 0 2px" }}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.12em", color: "#D4AF37", textTransform: "uppercase" }}>
            Mejores Cuotas
          </span>
          <span style={{ fontSize: 9, color: "#3F3F46" }}>8 casas internacionales</span>
        </div>
        <div style={{ display: "flex", gap: 5 }}>
          <button onClick={prev} aria-label="Anterior" data-testid="carousel-prev-btn" style={btnStyle}>
            <CaretLeft size={10} weight="bold" />
          </button>
          <button onClick={next} aria-label="Siguiente" data-testid="carousel-next-btn" style={btnStyle}>
            <CaretRight size={10} weight="bold" />
          </button>
        </div>
      </div>

      {/* Cards */}
      <div ref={wrapperRef} style={{ overflow: "hidden" }}>
        <div
          className="carousel-track"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          style={{
            display: "flex",
            transform: `translateX(${translateXpx}px)`,
            transition: "transform 0.45s cubic-bezier(0.4, 0, 0.2, 1)",
            willChange: "transform",
          }}
        >
          {BOOKMAKERS.map((book) => (
            <BookmakerCard key={book.id} book={book} cardWidth={cardWidth} />
          ))}
        </div>
      </div>

    </section>
  );
}
